import React, { useContext, useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import { MdArrowBack, MdLibraryAdd, MdSave } from 'react-icons/md';
import { toast } from '../../toasts';
import { Store } from '../../Store';
import { Button, FormField, Section, Select, controlClass, textareaClass } from '../../components/EntityList';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import MessageBox from '../../components/MessageBox';
import debtService from '../../services/debt.service';
import interestService from '../../services/interest.service';
import { getError } from '../../utils';

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

const FIELD_LABELS = {
	percentage: 'Porcentaje',
	type: 'Tipo',
	debt: 'Deuda vinculada',
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, ...action.payload, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'SAVE_REQUEST':
		return { ...state, saving: true };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false };
	case 'SAVE_FAIL':
		return { ...state, saving: false };
	default:
		return state;
	}
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const debtLabel = (debt) => {
	const id = debt.customId || debt.internalId || debt._id;
	const owner = debt.owner?.name;
	return [owner, id].filter(Boolean).join(': ');
};

function InterestEditor({ mode, interestId, lockedDebtId, onCancel, onSaved, saveLabel }) {
	const isEdit = mode === 'edit';
	const { dispatch: ctxDispatch } = useContext(Store);
	const [{ loading, saving, error: loadError, debts, interest }, dispatch] = useReducer(reducer, {
		debts: [],
		interest: {},
		loading: true,
		saving: false,
		error: '',
	});

	const [percentage, setPercentage] = useState('');
	const [type, setType] = useState('');
	const [debt, setDebt] = useState(lockedDebtId || '');
	const [folder, setFolder] = useState('');
	const [observations, setObservations] = useState('');
	const [fieldErrors, setFieldErrors] = useState({});
	const [formError, setFormError] = useState('');

	useEffect(() => {
		let isCurrent = true;
		const fetchData = async () => {
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				const [debtsResult, interestResult] = await Promise.all([
					debtService.list(),
					isEdit ? interestService.getDetails(interestId) : Promise.resolve(null),
				]);
				if (!isCurrent) return;
				const nextInterest = interestResult?.data || {};
				dispatch({
					type: 'FETCH_SUCCESS',
					payload: {
						debts: Array.isArray(debtsResult.data) ? debtsResult.data : [],
						interest: nextInterest,
					},
				});
				if (isEdit) {
					setPercentage(nextInterest.percentage ?? '');
					setType(nextInterest.type || '');
					setDebt(nextInterest.debt?._id || nextInterest.debt || '');
					setFolder(nextInterest.folder || '');
					setObservations(nextInterest.observations || '');
				} else {
					setDebt(lockedDebtId || '');
				}
			} catch (err) {
				if (isCurrent) dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
			}
		};
		fetchData();
		return () => {
			isCurrent = false;
		};
	}, [isEdit, interestId, lockedDebtId]);

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const validate = () => {
		const errors = {};
		if (percentage === '' || percentage === null || percentage === undefined) errors.percentage = REQUIRED_FIELD_MESSAGE;
		if (!String(type || '').trim()) errors.type = REQUIRED_FIELD_MESSAGE;
		if (!debt) errors.debt = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	const showMissingFields = (errors) => {
		const missingLabels = Object.keys(errors).map((field) => FIELD_LABELS[field] || field);
		const message = `Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`;
		setFormError(message);
		toast.error(message);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (saving) {
			return;
		}

		const errors = validate();
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			showMissingFields(errors);
			return;
		}

		const payload = {
			percentage,
			type: type.trim(),
			debt,
			folder: folder || '',
			observations: observations || '',
		};

		try {
			dispatch({ type: 'SAVE_REQUEST' });
			const response = isEdit
				? await interestService.edit(interest._id || interestId, payload)
				: await interestService.create(payload);
			dispatch({ type: 'SAVE_SUCCESS' });
			setFormError('');
			setFieldErrors({});
			if (isEdit) {
				ctxDispatch({ type: 'CONTACT_UPDATE_SUCCESS', payload: response.data });
				toast.success('Interes actualizado Correctamente');
			} else {
				toast.success('Interes creado');
			}
			onSaved(response.data?._id || interestId);
		} catch (err) {
			dispatch({ type: 'SAVE_FAIL' });
			toast.error(getError(err));
		}
	};

	if (loading) {
		return <DetailPageLoader entityLabel='interés' />;
	}

	if (loadError) {
		return <MessageBox variant='danger'>{loadError}</MessageBox>;
	}

	const debtOptions = debts.map((debtOption) => ({
		value: debtOption._id,
		label: debtLabel(debtOption),
	}));

	return (
		<Form id='interest-editor-form' onSubmit={handleSubmit} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{isEdit ? 'Actualizá los datos principales del interés.' : 'Completá los datos para registrar el interés.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving}>
						Cancelar
					</Button>
					<Button
						variant='primary'
						icon={isEdit ? <MdSave /> : <MdLibraryAdd />}
						type='submit'
						disabled={saving}
					>
						{saving ? 'Guardando...' : saveLabel}
					</Button>
				</div>
			</div>

			{formError && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{formError}
				</div>
			)}

			<Section title='Datos del interés'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='interest-percentage' label={FIELD_LABELS.percentage} required>
						<Form.Control
							id='interest-percentage'
							type='number'
							step='any'
							className={controlClass}
							value={percentage}
							disabled={saving}
							onChange={(event) => {
								setPercentage(event.target.value);
								clearFieldError('percentage');
							}}
						/>
						<FieldError message={fieldErrors.percentage} />
					</FormField>
					<FormField id='interest-type' label={FIELD_LABELS.type} required>
						<Form.Control
							id='interest-type'
							className={controlClass}
							value={type}
							disabled={saving}
							onChange={(event) => {
								setType(event.target.value);
								clearFieldError('type');
							}}
						/>
						<FieldError message={fieldErrors.type} />
					</FormField>
					<FormField id='interest-debt' label={FIELD_LABELS.debt} required className='md:tw:col-span-2'>
						<Select
							id='interest-debt'
							options={debtOptions}
							value={debt}
							disabled={saving || !!lockedDebtId}
							placeholder='Seleccione una deuda'
							onChange={(event) => {
								setDebt(event.target.value);
								clearFieldError('debt');
							}}
						/>
						<FieldError message={fieldErrors.debt} />
					</FormField>
				</div>
			</Section>

			<Section title='Organización'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='interest-folder' label='Carpeta'>
						<Form.Control
							id='interest-folder'
							className={controlClass}
							value={folder}
							disabled={saving}
							onChange={(event) => setFolder(event.target.value)}
						/>
					</FormField>
				</div>
			</Section>

			<Section title='Observaciones'>
				<FormField id='interest-observations' label='Observaciones'>
					<Form.Control
						id='interest-observations'
						as='textarea'
						className={textareaClass}
						value={observations}
						disabled={saving}
						onChange={(event) => setObservations(event.target.value)}
					/>
				</FormField>
			</Section>
		</Form>
	);
}

InterestEditor.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']).isRequired,
	interestId: PropTypes.string,
	lockedDebtId: PropTypes.string,
	onCancel: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	saveLabel: PropTypes.string.isRequired,
};

export default InterestEditor;
